import { ref, watch, watchEffect } from "vue";
import { message } from "@/utils/message-manager";
import type { MessageConfig } from "@/utils/message-manager";
import type { MessageParams, MessageHandler } from "element-plus";

/**
 * 消息管理器
 *
 */
export function useMessage() {
  /**
   * 消息管理器实例的响应式引用
   */
  const messageRef = ref(message);

  /**
   * 配置响应式引用
   */
  const config = ref<Partial<MessageConfig>>({
    max: 3,
    grouping: true,
    debounceTime: 500,
  });

  /**
   * 更新配置并应用到消息管理器
   */
  const updateConfig = (newConfig: Partial<MessageConfig>) => {
    config.value = { ...config.value, ...newConfig };
    message.setConfig(config.value);
  };

  /**
   * 响应式配置的watch
   */
  watch(
    config,
    (newConfig) => {
      message.setConfig(newConfig);
    },
    { deep: true }
  );

  /**
   * 消息队列状态的响应式引用
   */
  const queueState = ref({
    count: 0,
    messages: [] as string[],
    config: { ...config.value } as MessageConfig,
  });

  /**
   * 更新队列状态
   */
  const updateQueueState = () => {
    const state = message.getQueueState();
    queueState.value = {
      count: state.count,
      messages: [...state.messages],
      config: { ...state.config },
    };
  };

  /**
   * 自动更新队列状态的watch
   */
  watchEffect(() => {
    // 当消息管理器状态变化时自动更新
    updateQueueState();
  });

  /**
   * 包装的消息方法，自动更新状态
   */
  const messageMethods = {
    success: (content: string, options?: MessageParams): MessageHandler | null => {
      const result = message.success(content, options);
      updateQueueState();
      return result;
    },

    error: (content: string, options?: MessageParams): MessageHandler | null => {
      const result = message.error(content, options);
      updateQueueState();
      return result;
    },

    warning: (content: string, options?: MessageParams): MessageHandler | null => {
      const result = message.warning(content, options);
      updateQueueState();
      return result;
    },

    info: (content: string, options?: MessageParams): MessageHandler | null => {
      const result = message.info(content, options);
      updateQueueState();
      return result;
    },

    clearAll: (): void => {
      message.clearAll();
      updateQueueState();
    },
  };

  return {
    // 消息管理器实例
    message: messageRef,

    // 配置相关
    config,
    updateConfig,

    // 队列状态
    queueState,

    // 消息方法
    ...messageMethods,

    // 工具方法
    getQueueState: message.getQueueState,
    setConfig: message.setConfig,
  };
}

/**
 * Hook返回类型定义
 */
export type UseMessageReturn = ReturnType<typeof useMessage>;
